/**
 * CollectionsAdmin — accountant/owner-facing tab for the Tally 'Sundry
 * Debtors' upload flow + owner overview.
 *
 * Two panes:
 *   1. UploadPane: dropzone → parse → mapping table → apply
 *   2. OverviewPane: per-salesperson totals, trend, silent-staff alert
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Upload, IndianRupee, Users, TrendingDown, AlertTriangle, ChevronRight, CheckCircle2, FileSpreadsheet, Send, Trash2, AlertOctagon } from 'lucide-react';
import { api, formatINR, useAuth } from '../../lib/store';
import { logger } from '../../lib/logger';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const short = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '₹0';
  const a = Math.abs(n);
  if (a >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (a >= 100000)   return `₹${(n / 100000).toFixed(2)}L`;
  if (a >= 1000)     return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n)}`;
};

export default function CollectionsAdmin() {
  // A monotonically-increasing "reload key" — bumped after every
  // successful snapshot apply so OverviewPane can refetch without a
  // full-page reload. This is the lightweight alternative to
  // React Query invalidation for a single component.
  const [refreshKey, setRefreshKey] = useState(0);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isAdminOrManager = ['admin', 'manager'].includes(user?.role);
  return (
    <div className="space-y-5" data-testid="collections-admin">
      <div>
        <h2 className="text-lg sm:text-xl font-bold text-[#1A1C16]">Collections</h2>
        <p className="text-xs text-[#5C6150]">
          Upload the weekly Tally &lsquo;Sundry Debtors&rsquo; Excel to route outstanding customers to each salesperson. Gentle, opt-in reminders — never mass-blasted.
        </p>
      </div>
      <UploadPane onSnapshotSaved={() => setRefreshKey(k => k + 1)} />
      <OverviewPane refreshKey={refreshKey} isAdmin={isAdmin} onDataDeleted={() => setRefreshKey(k => k + 1)} />
      {isAdmin && <SnapshotManagementPane refreshKey={refreshKey} onDeleted={() => setRefreshKey(k => k + 1)} />}
      {isAdminOrManager && <EnrolmentPanel isAdmin={isAdmin} />}
    </div>
  );
}

// =====================================================================
// EnrolmentPanel — small opt-in toggle list. Only enrolled staff see
// the "Collect" nav item + the /my-collections page. Non-enrolled staff
// still get their sheets parsed, but the feature stays out of their way.
// =====================================================================
function EnrolmentPanel({ isAdmin }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    api.getSalespeople()
      .then(r => setUsers(r.data?.salespeople || []))
      .catch(e => logger.warn(e))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // We need the collections_enabled flag which /salespeople doesn't
  // return — fetch it in parallel from /admin/users? Falls back to
  // /collections/user/{id}. For simplicity, we probe the /me endpoint
  // per user isn't scalable. Use a joint endpoint: getCollectionsAdmin
  // already returns the enrolled list, but for a full list we can
  // rely on the enroll endpoint returning the new state.
  const toggle = async (u) => {
    setSaving(s => ({ ...s, [u.id]: true }));
    try {
      const next = !u.collections_enabled;
      const res = await api.setCollectionsEnrolment(u.id, next);
      setUsers(list => list.map(x => x.id === u.id ? { ...x, collections_enabled: res.data.collections_enabled } : x));
      toast.success(next ? `${u.name} enrolled — they'll see the Collect tab` : `${u.name} unenrolled`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(s => ({ ...s, [u.id]: false }));
    }
  };

  const toggleUploader = async (u) => {
    setSaving(s => ({ ...s, [`up-${u.id}`]: true }));
    try {
      const next = !u.collections_uploader;
      const res = await api.setCollectionsUploader(u.id, next);
      setUsers(list => list.map(x => x.id === u.id ? { ...x, collections_uploader: res.data.collections_uploader } : x));
      toast.success(next
        ? `${u.name} can now upload the weekly Debtors Excel from their login`
        : `${u.name}'s upload rights removed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(s => ({ ...s, [`up-${u.id}`]: false }));
    }
  };

  return (
    <Card className="border-[#E2E6D9]" data-testid="collections-enrolment-card">
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
          <Users className="w-4 h-4 text-[#556B2F]" /> Enrolled Staff (opt-in)
        </CardTitle>
        <p className="text-[11px] text-[#8F9485] mt-1">
          Only enrolled staff see the &lsquo;Collect&rsquo; navigation link. Toggle to opt someone in or out — takes effect on their next page load.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {loading && <Skeleton className="h-24 w-full" />}
        {!loading && users.length === 0 && (
          <p className="text-sm text-[#8F9485] py-2">No staff/manager users yet.</p>
        )}
        {!loading && users.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {users.map(u => (
              <div key={u.id} className="flex items-center justify-between gap-2 border border-[#E2E6D9] rounded-md px-3 py-2 bg-white">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1A1C16] truncate">{u.name || u.email}</p>
                  <p className="text-[10px] text-[#8F9485]">{(u.role || '').toUpperCase()}{u.staff_code ? ` · ${u.staff_code}` : ''}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant={u.collections_uploader ? 'default' : 'outline'}
                      onClick={() => toggleUploader(u)}
                      disabled={saving[`up-${u.id}`]}
                      title="Can upload the weekly Debtors Excel + see the full overview"
                      className={u.collections_uploader ? 'bg-[#8B6914] hover:bg-[#6E520F] text-white text-xs h-7' : 'text-xs h-7 border-[#DAA520]/40 text-[#8B6914]'}
                      data-testid={`uploader-toggle-${u.id}`}
                    >
                      {saving[`up-${u.id}`] ? '…' : (u.collections_uploader ? 'Uploader ✓' : 'Upload rights')}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant={u.collections_enabled ? 'default' : 'outline'}
                    onClick={() => toggle(u)}
                    disabled={saving[u.id]}
                    className={u.collections_enabled ? 'bg-[#556B2F] hover:bg-[#3F5222] text-white text-xs h-7' : 'text-xs h-7'}
                    data-testid={`enrol-toggle-${u.id}`}
                  >
                    {saving[u.id] ? '…' : (u.collections_enabled ? 'Enrolled' : 'Enroll')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// UploadPane — dropzone + mapping table + apply
// =====================================================================
function UploadPane({ onSnapshotSaved }) {
  const [stage, setStage] = useState(null); // {stage_id, sheets, users, ...}
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [mapping, setMapping] = useState({}); // sheet_name -> user_id | ''

  const onFile = useCallback(async (file) => {
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) {
      toast.error('Please upload an .xlsx file exported from Tally.');
      return;
    }
    setUploading(true);
    setStage(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.uploadDebtorsExcel(fd);
      setStage(res.data);
      // Seed mapping from proposed_user_id
      const seed = {};
      (res.data.sheets || []).forEach(s => { seed[s.sheet_name] = s.proposed_user_id || ''; });
      setMapping(seed);
      toast.success(`Parsed ${res.data.total_customers} customers across ${res.data.sheets.length} sheets.`);
    } catch (err) {
      logger.error(err);
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) onFile(file);
  };

  const applyMapping = async () => {
    if (!stage) return;
    // Warn if any sheet is unassigned — allow but confirm.
    const unassigned = (stage.sheets || []).filter(s => !mapping[s.sheet_name]);
    if (unassigned.length > 0) {
      const ok = window.confirm(
        `${unassigned.length} sheet(s) are unassigned and won't be routed to any salesperson: ${unassigned.map(s => s.sheet_name).join(', ')}\n\nApply anyway?`
      );
      if (!ok) return;
    }
    setApplying(true);
    try {
      const clean = {};
      Object.entries(mapping).forEach(([k, v]) => { clean[k] = v || null; });
      const res = await api.applyDebtorMapping(stage.stage_id, clean);
      toast.success(`Snapshot saved — ${res.data.rows_inserted} customers routed.`);
      setStage(null);
      setMapping({});
      if (onSnapshotSaved) onSnapshotSaved();
    } catch (err) {
      logger.error(err);
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card className="border-[#E2E6D9]" data-testid="collections-upload-card">
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-[#556B2F]" /> Weekly Tally Upload
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {!stage && (
          <label
            className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#DAA520]/60 bg-[#FFFBF0] py-8 cursor-pointer hover:border-[#556B2F] hover:bg-[#F9FAF7] transition-colors"
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            data-testid="collections-dropzone"
          >
            <Upload className="w-8 h-8 text-[#556B2F]" />
            <p className="text-sm text-[#1A1C16] font-medium">
              {uploading ? 'Parsing Excel…' : 'Drop the Tally Sundry Debtors .xlsx here'}
            </p>
            <p className="text-[11px] text-[#8F9485]">or click to browse — we&apos;ll auto-detect each salesperson&apos;s sheet</p>
            <input
              type="file"
              accept=".xlsx,.xlsm"
              className="hidden"
              disabled={uploading}
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid="collections-file-input"
            />
          </label>
        )}
        {stage && (
          <div className="space-y-3" data-testid="collections-mapping">
            <div className="flex items-center flex-wrap gap-2 text-[11px]">
              <Badge className="bg-[#556B2F] text-white">{stage.total_customers} customers</Badge>
              <Badge className="bg-[#DAA520] text-white">{formatINR(stage.total_amount)}</Badge>
              <Badge variant="outline">{stage.sheets.length} sheets</Badge>
              {(stage.warnings || []).map((w, i) => (
                <Badge key={i} variant="outline" className="border-[#B93838]/40 text-[#B93838]">{w}</Badge>
              ))}
            </div>

            <div className="overflow-x-auto rounded-md border border-[#E2E6D9]">
              <table className="w-full text-sm">
                <thead className="bg-[#F9FAF7] text-[10px] uppercase tracking-wider text-[#5C6150]">
                  <tr>
                    <th className="text-left py-2 px-3">Sheet</th>
                    <th className="text-right py-2 px-3">Customers</th>
                    <th className="text-right py-2 px-3">Total</th>
                    <th className="text-left py-2 px-3 min-w-[220px]">Route to salesperson</th>
                  </tr>
                </thead>
                <tbody>
                  {stage.sheets.map(s => (
                    <tr key={s.sheet_name} className="border-t border-[#E2E6D9]" data-testid={`mapping-row-${s.sheet_name}`}>
                      <td className="py-2 px-3 font-medium">{s.sheet_name}</td>
                      <td className="py-2 px-3 text-right font-mono-data">{s.customer_count}</td>
                      <td className="py-2 px-3 text-right font-mono-data font-semibold text-[#556B2F]">{formatINR(s.total_amount)}</td>
                      <td className="py-2 px-3">
                        <Select
                          value={mapping[s.sheet_name] || ''}
                          onValueChange={(v) => setMapping(m => ({ ...m, [s.sheet_name]: v === '__unassigned__' ? '' : v }))}
                        >
                          <SelectTrigger className="h-8 text-xs border-[#E2E6D9]" data-testid={`mapping-select-${s.sheet_name}`}>
                            <SelectValue placeholder="Select salesperson…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__unassigned__">— Unassigned (skip) —</SelectItem>
                            {stage.users.map(u => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.name}
                                {u.staff_code ? ` (${u.staff_code})` : ''}
                                {u.collections_enabled ? ' · ✓ enrolled' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={applyMapping}
                disabled={applying}
                className="bg-[#556B2F] hover:bg-[#3F5222] text-white"
                data-testid="collections-apply-btn"
              >
                {applying ? 'Saving…' : 'Approve & Save Snapshot'}
                <CheckCircle2 className="w-4 h-4 ml-1" />
              </Button>
              <Button
                variant="ghost"
                onClick={() => { setStage(null); setMapping({}); }}
                data-testid="collections-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// OverviewPane — owner-side leaderboard + trend + silent staff
// =====================================================================
function OverviewPane({ refreshKey, isAdmin }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingDigest, setSendingDigest] = useState(false);

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await api.sendCollectionsDigest();
      const sent = (res.data.recipients || []).filter(r => r.sent).length;
      const skipped = (res.data.recipients || []).length - sent;
      if (res.data.sent) {
        toast.success(`Digest sent to ${sent} staff${skipped ? ` (${skipped} skipped)` : ''}.`);
      } else {
        toast.warning(res.data.reason || 'No digests sent — check staff phones & DoubleTick key.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Digest send failed');
    } finally {
      setSendingDigest(false);
    }
  };

  const reload = useCallback(() => {
    setLoading(true);
    api.getCollectionsAdmin()
      .then(r => setData(r.data))
      .catch(e => logger.warn('Failed to load collections admin:', e))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { reload(); }, [reload, refreshKey]);

  if (loading) return <Skeleton className="h-40 w-full" />;
  if (!data || !data.snapshot_id) {
    return (
      <Card className="border-[#E2E6D9]">
        <CardContent className="py-8 text-center text-[#8F9485] text-sm">
          No debtor snapshot yet. Upload the first Tally Excel above to get started.
        </CardContent>
      </Card>
    );
  }

  const trend = data.trend || [];
  const trendDelta = trend.length >= 2
    ? trend[trend.length - 1].total_amount - trend[trend.length - 2].total_amount
    : 0;

  return (
    <Card className="border-[#E2E6D9]" data-testid="collections-overview-card">
      <CardHeader className="py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
              <IndianRupee className="w-4 h-4 text-[#DAA520]" /> Current Snapshot
            </CardTitle>
            <p className="text-[11px] text-[#8F9485] mt-1">
              {new Date(data.snapshot_created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
              {' '}· staff digest goes out every Monday 11:00 AM
            </p>
          </div>
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              onClick={sendDigest}
              disabled={sendingDigest}
              className="text-xs h-7 border-[#556B2F]/40 text-[#556B2F]"
              data-testid="send-digest-now-btn"
            >
              <Send className="w-3 h-3 mr-1" /> {sendingDigest ? 'Sending…' : 'Send digest now'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatBox label="Total pending" value={formatINR(data.total_amount)} accent="bg-[#556B2F]" icon={IndianRupee} />
          <StatBox label="Customers" value={data.total_customers.toLocaleString('en-IN')} accent="bg-[#DAA520]" icon={Users} />
          <StatBox
            label="Vs last upload"
            value={trend.length >= 2 ? (trendDelta <= 0 ? `↓ ${short(-trendDelta)}` : `↑ ${short(trendDelta)}`) : '—'}
            sub={trend.length >= 2 && trendDelta <= 0 ? 'shrinking 🎯' : trend.length >= 2 ? 'growing' : 'baseline'}
            accent={trend.length >= 2 && trendDelta <= 0 ? 'bg-[#2E7D32]' : 'bg-[#B93838]'}
            icon={TrendingDown}
          />
          <StatBox
            label="Silent staff (7d)"
            value={(data.silent_staff || []).length.toString()}
            sub="no action logged"
            accent={(data.silent_staff || []).length > 0 ? 'bg-[#B93838]' : 'bg-[#2E7D32]'}
            icon={AlertTriangle}
          />
        </div>

        {/* Leaderboard */}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[#8F9485] font-semibold mb-2">Per Salesperson (click to drill through)</p>
          <div className="overflow-x-auto rounded-md border border-[#E2E6D9]">
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAF7] text-[10px] uppercase tracking-wider text-[#5C6150]">
                <tr>
                  <th className="text-left py-2 px-3">Salesperson</th>
                  <th className="text-left py-2 px-3">From sheet</th>
                  <th className="text-right py-2 px-3">Customers</th>
                  <th className="text-right py-2 px-3">Pending</th>
                  <th className="text-right py-2 px-3 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(data.salespeople || []).map(r => {
                  const isUnassigned = r.user_id === '__unassigned__';
                  return (
                    <tr
                      key={r.user_id}
                      className={`border-t border-[#E2E6D9] hover:bg-[#F9FAF7] ${isUnassigned ? '' : 'cursor-pointer'}`}
                      onClick={() => { if (!isUnassigned) navigate(`/admin/collections/${r.user_id}`); }}
                      data-testid={`collections-sp-row-${r.user_id}`}
                    >
                      <td className="py-2 px-3 font-medium text-[#1A1C16]">
                        {r.user_name || 'Unassigned'}
                        {isUnassigned && <Badge variant="outline" className="ml-2 text-[10px] border-[#B93838]/40 text-[#B93838]">skip</Badge>}
                      </td>
                      <td className="py-2 px-3 text-[#8F9485] text-xs">{r.sheet_name || '—'}</td>
                      <td className="py-2 px-3 text-right font-mono-data">{r.count}</td>
                      <td className="py-2 px-3 text-right font-mono-data font-semibold text-[#556B2F]">{formatINR(r.total)}</td>
                      <td className="py-2 px-3 text-[#8F9485]">{!isUnassigned && <ChevronRight className="w-4 h-4" />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {(data.silent_staff || []).length > 0 && (
          <div className="rounded-md border border-[#B93838]/40 bg-[#FDECEA] p-3 text-xs" data-testid="silent-staff-banner">
            <p className="font-semibold text-[#7A1F12] mb-1">⚠ Silent staff — no action logged in 7 days</p>
            <ul className="list-disc ml-5 text-[#7A1F12]">
              {data.silent_staff.map(s => (
                <li key={s.user_id}>
                  {s.name}{s.staff_code ? ` (${s.staff_code})` : ''}
                  {s.last_action_at
                    ? ` — last action ${new Date(s.last_action_at).toLocaleDateString('en-IN')}`
                    : ' — no actions ever logged'}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatBox({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="border border-[#E2E6D9] rounded-lg p-3 bg-white" data-testid={`collections-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-[9px] uppercase tracking-wider text-[#8F9485] font-medium">{label}</p>
          <p className="text-lg sm:text-xl font-bold font-mono-data text-[#1A1C16] mt-0.5 truncate">{value}</p>
          {sub && <p className="text-[10px] text-[#5C6150] mt-0.5">{sub}</p>}
        </div>
        <div className={`w-7 h-7 rounded-md flex-shrink-0 flex items-center justify-center ${accent}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
    </div>
  );
}


// =====================================================================
// SnapshotManagementPane — admin-only. List every uploaded snapshot and
// delete individual ones (or nuke everything) to clean up test data
// before the real Tally list goes live. Iter-123.
// =====================================================================
function SnapshotManagementPane({ refreshKey, onDeleted }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [wiping, setWiping] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.listCollectionSnapshots()
      .then(r => setSnapshots(r.data?.snapshots || []))
      .catch(e => logger.warn('Failed to list snapshots:', e))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const deleteOne = async (snap) => {
    if (!window.confirm(
      `Delete this snapshot?\n\nUploaded: ${new Date(snap.created_at).toLocaleString('en-IN')}\n` +
      `Customers: ${snap.total_customers} · ${formatINR(snap.total_amount)}\n\n` +
      `All rows and logged actions for this snapshot will be removed. This can't be undone.`
    )) return;
    setDeletingId(snap.id);
    try {
      const res = await api.deleteCollectionSnapshot(snap.id);
      toast.success(`Deleted — ${res.data.rows_deleted} customers, ${res.data.actions_deleted} actions removed.`);
      onDeleted?.();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete snapshot');
    } finally {
      setDeletingId(null);
    }
  };

  const wipeAll = async () => {
    const confirmText = `DELETE ALL`;
    const typed = window.prompt(
      `⚠ This will PERMANENTLY delete every collections snapshot, all customer rows, and every logged staff action.\n\n` +
      `Type "${confirmText}" (case-sensitive) to confirm:`
    );
    if (typed !== confirmText) {
      toast.info('Cancelled — nothing was deleted.');
      return;
    }
    setWiping(true);
    try {
      const res = await api.deleteAllCollections();
      toast.success(
        `Wiped clean — ${res.data.snapshots_deleted} snapshots, ${res.data.rows_deleted} rows, ${res.data.actions_deleted} actions.`
      );
      onDeleted?.();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wipe failed');
    } finally {
      setWiping(false);
    }
  };

  return (
    <Card className="border-[#E2E6D9]" data-testid="collections-snapshot-mgmt">
      <CardHeader className="py-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-sm font-bold uppercase tracking-wide flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-[#556B2F]" /> Snapshot History &amp; Cleanup
          </CardTitle>
          {snapshots.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={wipeAll}
              disabled={wiping}
              className="h-7 text-xs border-[#B93838]/40 text-[#B93838] hover:bg-[#B93838] hover:text-white"
              data-testid="collections-wipe-all-btn"
            >
              <AlertOctagon className="w-3.5 h-3.5 mr-1" />
              {wiping ? 'Wiping…' : 'Wipe all test data'}
            </Button>
          )}
        </div>
        <p className="text-[11px] text-[#8F9485] mt-1">
          Delete individual test uploads (or wipe everything) before assigning the real Tally list to actual staff.
        </p>
      </CardHeader>
      <CardContent className="pt-0">
        {loading && <Skeleton className="h-16 w-full" />}
        {!loading && snapshots.length === 0 && (
          <p className="text-xs text-[#8F9485] py-2">No snapshots yet — nothing to clean up.</p>
        )}
        {!loading && snapshots.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-[#E2E6D9]">
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAF7] text-[10px] uppercase tracking-wider text-[#5C6150]">
                <tr>
                  <th className="text-left py-2 px-3">Uploaded</th>
                  <th className="text-right py-2 px-3">Customers</th>
                  <th className="text-right py-2 px-3">Total</th>
                  <th className="text-right py-2 px-3 w-24">Action</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map((s, idx) => (
                  <tr key={s.id} className="border-t border-[#E2E6D9]" data-testid={`snapshot-row-${s.id}`}>
                    <td className="py-2 px-3">
                      <div className="text-[#1A1C16]">{new Date(s.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                      {idx === 0 && (
                        <Badge className="mt-0.5 bg-[#2E7D32] text-white text-[9px] py-0 px-1.5">latest · in use</Badge>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right font-mono-data">{s.total_customers}</td>
                    <td className="py-2 px-3 text-right font-mono-data font-semibold text-[#556B2F]">{formatINR(s.total_amount)}</td>
                    <td className="py-2 px-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteOne(s)}
                        disabled={deletingId === s.id}
                        className="h-7 text-xs text-[#B93838] hover:bg-[#FDECEA]"
                        data-testid={`snapshot-delete-btn-${s.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        {deletingId === s.id ? '…' : 'Delete'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
